import { Table as MuiTable, TableBody } from '@mui/material';

import { TableHeader } from './TableHeader';
import { TableStickyHeader } from './TableStickyHeader';
import { TableRowComp as TableRow } from './TableRow';
import {
  TablePaginationComp as TablePagination,
  PaginationConfig,
} from './TablePagination';
import { TableSkeleton } from './TableSkeleton';

import { SelectedSort } from '@/hooks/useSortableData';

export type ColumnConfig = {
  label: string;
  field: string;
  render?: (value: any, rowData: any) => React.ReactNode;
  sortable?: boolean;
};

export type TableProps = {
  columns: ColumnConfig[];
  data: any[];
  renderActions?: (rowData: any) => React.ReactNode;
  /** Dónde va la columna de acciones. Por defecto al final. */
  actionsPosition?: 'start' | 'end';
  pagination?: PaginationConfig;
  isLoading?: boolean;
  skeletonRows?: number;
  isGhost?: boolean;
  handleSort?: (key: string) => void;
  selectedSort?: SelectedSort;
  stickyHeader?: boolean;
  selectable?: boolean;
  isRowSelectable?: (rowData: Record<string, unknown>) => boolean;
  selectedKeys?: Set<string>;
  maxSelectable?: number;
  onSelectAll?: (checked: boolean) => void;
  onSelectRow?: (key: string, checked: boolean) => void;
  getRowKey?: (rowData: Record<string, any>) => string;
  getRowStyle?: (rowData: any) => React.CSSProperties;

  onRowReorder?: (fromIndex: number, toIndex: number) => void;
};

const Table = ({
  columns,
  data,
  renderActions,
  actionsPosition = 'end',
  pagination,
  isLoading,
  skeletonRows = 10,
  isGhost = false,
  selectedSort,
  handleSort,
  stickyHeader = false,
  selectable,
  isRowSelectable,
  selectedKeys,
  maxSelectable,
  onSelectAll,
  onSelectRow,
  getRowKey,
  getRowStyle,
  onRowReorder,
}: TableProps) => {
  const Header = stickyHeader ? TableStickyHeader : TableHeader;

  const selectableRows = isRowSelectable
    ? data.filter((row) => isRowSelectable(row as Record<string, unknown>))
    : data;

  const isAllSelected =
    selectable &&
    selectedKeys &&
    selectableRows.length > 0 &&
    selectableRows.every((rowData) => {
      const key = getRowKey
        ? getRowKey(rowData)
        : String(rowData.id || rowData.number || '');
      return selectedKeys.has(key);
    });

  return (
    <>
      <MuiTable stickyHeader={stickyHeader}>
        <Header
          columns={columns}
          renderActions={renderActions}
          actionsPosition={actionsPosition}
          isGhost={isGhost}
          selectedSort={selectedSort}
          handleSort={handleSort}
          selectable={selectable}
          selectedKeys={selectedKeys}
          data={data}
          maxSelectable={maxSelectable}
          isAllSelected={isAllSelected}
          onSelectAll={onSelectAll}
        />
        <TableBody>
          {isLoading ? (
            <TableSkeleton
              columns={columns}
              renderActions={renderActions}
              skeletonRows={skeletonRows}
              selectable={selectable}
            />
          ) : (
            data.map((rowData, rowIndex) => {
              const rowKey = getRowKey
                ? getRowKey(rowData)
                : String(rowData.id || rowData.number || rowIndex);
              return (
                <TableRow
                  key={rowIndex}
                  rowData={rowData}
                  columns={columns}
                  renderActions={renderActions}
                  actionsPosition={actionsPosition}
                  rowIndex={rowIndex}
                  isGhost={isGhost}
                  selectable={selectable}
                  isRowSelectable={isRowSelectable?.(rowData as Record<string, unknown>)}
                  rowKey={rowKey}
                  selectedKeys={selectedKeys}
                  maxSelectable={maxSelectable}
                  onSelectRow={onSelectRow}
                  getRowStyle={getRowStyle}
                  onRowReorder={onRowReorder}
                />
              );
            })
          )}
        </TableBody>
      </MuiTable>
      {pagination && <TablePagination {...pagination} />}
    </>
  );
};

export default Table;
